import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link, useNavigate  } from 'react-router-dom'

import * as spriteIcons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import FriendRequestModal from 'components/UI/Modal/FriendRequest/FriendRequestModal'
import ProfileNotificationsModal from 'components/UI/Modal/ProfileNotifications/ProfileNotificationsModal'
import TipModal from 'components/UI/Modal/Tip/TipModal'
import { StoriesList } from 'components-ref/organisms'
import { useModalContext } from 'context/modalContext'
import { AllIcons } from 'helpers/allIcons'
import { Icons } from 'helpers/icons'
import { useAccessToken, useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { type IProfileInfo } from 'types/interfaces/IProfileInfo'

const formatCount = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === 'hidden') {
    return ''
  }

  const count = Number(value)

  if (!Number.isFinite(count)) return ''

  if (count < 1000) return String(count)

  if (count < 1000000) {
    const rounded = Math.round(count / 100) / 10
    return `${rounded.toFixed(1).replace(/\.0$/, '')}k`
  }

  const rounded = Math.round(count / 100000) / 10
  return `${rounded.toFixed(1).replace(/\.0$/, '')}m`
}

export const ProfileInfo = ({
  id,
  role,
  display_name,
  follower_count,
  friend_count,
  avatar,
  username,
  isSubscribed,
  friends_status,
  friendRequest,
  setSubscribeModalOpen,
  mini_bio,
  show_follower_count,
  public_friends,
  show_likes_count,
  show_mini_bio,
  post_like_count,
  friend_button,
  rebill,
  subscription_plans,
  promo_campaign,
}: IProfileInfo) => {

  const { t } = useTranslation()
  const userData = useUserProfile()
  const modalData = useModalContext()
  const isAuthenticated = useAccessToken() !== ''

  const profileCache = useProfileCacheStore(state => state.getProfile)
  const viewerId = userData?.id ?? null
  const cachedProfile = username
    ? profileCache(username, viewerId)
    : undefined

  const isOwnProfile = userData?.id === id

  const navigate = useNavigate()

  const conversationId =
    (cachedProfile as any)?.conversation_id ??
    (cachedProfile as any)?.conversationId ??
    null

  const rawAvatar = avatar as any

  const normalizedAvatar =
    typeof rawAvatar === 'string'
      ? { url: rawAvatar }
      : rawAvatar?.url
        ? rawAvatar
        : null

  const chatLinkState = {
    otherUserData: {
      id,
      username,
      name: display_name,
      display_name,
      avatar: normalizedAvatar,
      role,
      isSubscribed,
    },
    ...(conversationId ? { conversationId } : {}),
  }

  // useEffect is also a hook – it MUST come before any early return too
const isModelFriend =
  userData?.role === 'model' &&
  role === 'model' &&
  friends_status === 'Friends'

const shouldShowStandardSubscribe =
  !isSubscribed || (isModelFriend && rebill !== 1)

const plans =
  cachedProfile?.subscription_plans ??
  subscription_plans ??
  []

const monthlyPlan = plans.find(
  (plan: any) => Number(plan.month_count) === 1
)

const subscriptionPrice =
  cachedProfile?.promo_campaign?.discounted_price ??
  promo_campaign?.discounted_price ??
  monthlyPlan?.price ??
  null

const subscriptionDiscount =
  cachedProfile?.promo_campaign?.discount ??
  monthlyPlan?.discount ??
  null


  const canRenderFromProps = !!id && !!username

  if (!cachedProfile && !canRenderFromProps) return null

  // --- rest of component can safely assume cachedProfile exists ---

  const cardsArr: any[] = Array.isArray((userData as any)?.cards)
    ? (userData as any).cards
    : []
  const hasCards = cardsArr.length > 0
  const hasDefaultCard = Boolean((userData as any)?.default_card)
  const walletBalance = Number((userData as any)?.wallet_deposit ?? 0)
const walletOk =
  userData?.is_verified === true &&
  subscriptionPrice != null &&
  walletBalance >= Number(subscriptionPrice)

  const addTip = () => {
      modalData.addModal(
          t('tipAmount'),
          <TipModal
              modelData={{
                  modelId: id,
                  avatarSrc: avatar,
              }}
          />,
          false,
          false,
          '',
          '',
          true // keyboardAdjust
      )
  }

  const notificationsModal = () => {
    modalData.addModal(t('Notifications'), <ProfileNotificationsModal id={id} />)
  }

  const buttonsSwitcher = () => {
    if (userData?.id === id && role === 'model' && userData?.role === 'model') {
      return 'model'
    }
    if (userData?.id !== id && role === 'model' && userData?.role === 'model') {
      return 'model-on-model'
    }
    if (userData?.id === id && role === 'fan' && userData?.role === 'fan') {
      return 'fan'
    }
    if (userData?.id !== id && role === 'model' && userData?.role === 'fan') {
      return 'fan-on-model'
    }
    if (!userData?.id) {
      return 'not-logged'
    }
  }

const renderProfileInfoButton = () => {
  if (shouldShowStandardSubscribe) {
    if (!isOwnProfile && isAuthenticated) {
      const noWallet = walletBalance <= 0
      const noDefaultCard = !hasDefaultCard

      if (noWallet && noDefaultCard) {
        return buttonType('addCard')
      }
    }

    return hasCards || walletOk
      ? buttonType('subscribe')
      : buttonType('addCard')
  }

  return buttonType('subscribed')
}

  const buttonType = (type: string) => {
    switch (type) {
      case 'subscribe':
        return (
          <Button
            text={
              <>
                {t('subscribe')}{' '}
                  <b style={{ marginLeft: '4px' }}>
                    {subscriptionPrice === 0
                      ? 'Free'
                      : subscriptionPrice != null
                        ? `$${Number(subscriptionPrice).toFixed(2)}`
                        : ''}
                  </b>
                {!!subscriptionDiscount && (
                  <div className='profileholder__buttons__sub__discount'>
                    {t('save')} {subscriptionDiscount}%
                  </div>
                )}
              </>
            }
            color='blue'
            font='mont-14-normal'
            width='fit'
            padding='2'
            height='3'
            clickFn={() => {
              if (cachedProfile?.isBlocked) {
                addToast('error', t('You are Blocked by this user'))
                return
              }
              setSubscribeModalOpen(true)
            }}
            customClass='profileholder__buttons__sub'
          />
        )

      case 'addCard':
        return (
          <Link
            style={{ textDecoration: 'none' }}
            className='button button--blue button--mont-14-normal button--width-fit button--height-3 button--padding-2 profileholder__buttons__sub'
            to='/settings/general/wallet'
          >
            Add a Payment Card
          </Link>
        )

      case 'subscribed':
        return isSubscribed && rebill === 1 ? (
          <Button
            text={t('Subscribed')}
            color='transparent'
            type='transparent--dark1px'
            font='mont-14-normal'
            width='fit'
            padding='2'
            height='3'
            clickFn={() => {
              setSubscribeModalOpen(true)
            }}
            icon={AllIcons.profile_checkcircle}
            customClass='profileholder__buttons__sub'
          />
          ) : (
            <Button
              text={t(friends_status === 'Friends' ? 'Subscribe' : 'Resubscribe')}
              color='blue'
              font='mont-14-normal'
              width='fit'
              padding='2'
              height='3'
              clickFn={() => {
                setSubscribeModalOpen(true)
              }}
              customClass='profileholder__buttons__sub'
            />
          )

      case 'tip':
        return (
          <Button
            text={t('tip')}
            color='blue'
            font='mont-14-normal'
            width='fit'
            padding='2'
            height='3'
            clickFn={addTip}
            icon={Icons.coins}
            customClass='profileholder__buttons__sub'
          />
        )

      case 'Not friend':
          return userData?.friend_button && friend_button ? (
          <Button
            text={<img src={Icons.addUser} alt={t('addFriend')} />}
            color='blue'
            height='3'
            clickFn={() => {
              friendRequest(id)
            }}
            customClass='profileholder__buttons__round profileholder__buttons__round--add'
          />
        ) : (
          <></>
        )

      case 'Pending':
        return (
          <Button
            text={t('pending')}
            icon={AllIcons.profile_pending}
            color='transparent'
            type='transparent--dark1px'
            font='mont-14-normal'
            width='fit'
            padding='2'
            height='3'
            clickFn={() => {
              friendRequest(id)
            }}
          />
        )

      case 'Friends':
        return (
          <Button
            text={<img src={AllIcons.profile_friend} alt={t('friends')} />}
            color='transparent'
            type='transparent--dark1px'
            height='3'
            customClass='profileholder__buttons__round'
            clickFn={() => {
              modalData.addModal(
                t('Remove Friend'),
                <FriendRequestModal
                  id={id}
                  online={false}
                  avatar={avatar}
                  display_name={display_name}
                  username={username}
                  follower_count={follower_count}
                  friend_count={friend_count}
                  friends_status={friends_status}
                  mode="remove"
                  close={() => modalData.clearModal()}
                />
              )
            }}
          />
        )
    }
  }

  const renderPage = () => {
    switch (buttonsSwitcher()) {
      case 'model': {
        const stories: any[] = []
        return stories.length > 0 ? <StoriesList page='profile' stories={[]} /> : null
      }

case 'model-on-model': {
  const showNotifications =
    userData?.id !== id &&
    (
      isSubscribed ||
      friends_status === 'Friends'
    )

  return (
    <div className='profileholder__buttons'>
      {showNotifications && (
        <div
          className='profileholder__buttons__circle'
          style={{ marginRight: '10px' }}
          onClick={() => {
            notificationsModal()
          }}
        >
          <img
            src={AllIcons.profile_notification}
            alt={t('Notifications')}
          />
        </div>
      )}

      {renderProfileInfoButton()}

      {friend_button && buttonType(friends_status)}
    </div>
  )
}
      case 'fan-on-model':
        if (isSubscribed) {
          return (
            <div className='profileholder__buttons'>
              <div
                className='profileholder__buttons__circle'
                style={{ marginRight: '10px' }}
                onClick={() => {
                  notificationsModal()
                }}
              >
                <img src={AllIcons.profile_notification} alt={t('Notifications')} />
              </div>
              {buttonType('subscribed')}
              {buttonType('tip')}
            </div>
          )
        } else {
          return (
            <div className='profileholder__buttons'>{renderProfileInfoButton()}</div>
          )
        }

      case 'not-logged':
        return (
          <div className='profileholder__buttons'>
            {buttonType('subscribe')}
          </div>
        )

      default:
        return <div>Fallback page</div>
    }
  }

  const renderBubble = () => {
    switch (buttonsSwitcher()) {
      case 'model':
        return ''

      case 'model-on-model':
      if (
        (
          isSubscribed ||
          friends_status === 'Friends'
        ) &&
        userData?.id !== id
      ) {
          return (
            <Link
              to={`/chat/${id}`}
              state={chatLinkState}
              className='profileholder-chat-link'
            >
              <div className='profileholder-chat-link-inner'>
                <spriteIcons.IconChatBubbleWhite />
              </div>
            </Link>
          )
        }
        break
      case 'not-logged':
      case 'fan-on-model': {
        if (isSubscribed) {
          return (
            <Link
              to={`/chat/${id}`}
              state={chatLinkState}
              className='profileholder-chat-link'
            >
              <div className='profileholder-chat-link-inner'>
                <spriteIcons.IconChatBubbleWhite />
              </div>
            </Link>
          )
        }
        return null
      }

      default:
        return <div>Fallback bubble</div>
    }
  }

  return (
    <div className='profileholder__info'>
      <p className='profileholder__name'>{display_name}</p>
      <div className='profileholder__username'>
        {post_like_count != null && (
          <>
            <p className='profileholder__username--followers'>
              <span>{formatCount(post_like_count)}</span> {t('likes')}
            </p>
            <div className='profileholder__username--border'></div>
          </>
        )}
        {follower_count != null && (
          <>
            {isOwnProfile ? (
              <Link to='/fans' className='profileholder__username--followers-link'>
                <p className='profileholder__username--followers'>
                  <span>{formatCount(follower_count)}</span> {t('Fans')}
                </p>
              </Link>
            ) : (
              <p className='profileholder__username--followers'>
                <span>{formatCount(follower_count)}</span> {t('Fans')}
              </p>
            )}
            <div className='profileholder__username--border'></div>
          </>
        )}
        {public_friends && friend_button && (
          <Link to={`/model/${id}/friends`} className='profileholder__friends--link'>
            <p className='profileholder__username--friends'>
              <span>{formatCount(friend_count)}</span> {t('friends')}
            </p>
            <div className='profileholder__username--border'></div>
          </Link>
        )}
        <p
          className='profileholder__username--name'
          onClick={() => navigate('/settings/account/change-username')}
        >
          @{username}
        </p>
      </div>
      {mini_bio && mini_bio.trim() !== 'hidden' && (
        <p className='profileholder__quote'>{mini_bio}</p>
      )}

      {renderPage()}
      {renderBubble()}
    </div>
  )
}
